import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PUSH_NOTIFICATION_ACTION_LABELS, PushNotificationAction } from "@speakai/shared";
import { Button } from "@speakai/ui/button";
import { getMinutesUntilStart, type MeetingReminder } from "@/lib/meeting-reminders/reminder-payload";
import { FloatingActionCard } from "./floating-action-card";

const AUTO_DISMISS_MS = 120_000;
const CLOCK_TICK_MS = 60_000;

interface MeetingReminderCardProps {
  reminder: MeetingReminder;
  onJoin: (url: string) => void;
  onDismiss: (eventId: string) => void;
}

export function MeetingReminderCard({ reminder, onJoin, onDismiss }: MeetingReminderCardProps) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());
  const minutes = getMinutesUntilStart(reminder.startTime, now);

  useEffect(() => {
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, [reminder]);

  useEffect(() => {
    const timer = setTimeout(() => onDismiss(reminder.eventId), AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [onDismiss, reminder]);

  return (
    <FloatingActionCard
      title={reminder.title}
      subtitle={
        minutes > 0
          ? t("MEETING_REMINDERS.STARTS_IN", "Starts in {{minutes}} min", { minutes })
          : t("MEETING_REMINDERS.STARTING_NOW", "Starting now")
      }
      onDismiss={() => onDismiss(reminder.eventId)}
      action={
        reminder.meetingUrl && reminder.actions.includes(PushNotificationAction.JOIN) ? (
          <Button type="button" size="sm" onClick={() => onJoin(reminder.meetingUrl as string)}>
            {t("MEETING_ASSISTANT.JOIN", PUSH_NOTIFICATION_ACTION_LABELS[PushNotificationAction.JOIN])}
          </Button>
        ) : undefined
      }
    />
  );
}
